import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useCurrency } from '../../context/CurrencyContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';

const getPackageImage = (pkg) => pkg.thumbnail?.linkLocalizations?.[0]?.mediaLink ?? null;

const getPackageName = (pkg) => pkg.nameLocalizations?.values?.[0]?.value ?? '—';

/**
 * Same visual language as FreeTrialItemCard (image / name / price / footer), sourcing a
 * package's own fields (package has no item row — its price and thumbnail live directly on
 * the package itself, not under a merchandise/ticket/badge/avatar sub-object like an item's do).
 */
const FreeTrialPackageCard = ({ pkg, freeTrialEntry, onAdd, onRemove, canEdit = true, busy = false }) => {
  const { activeCurrency, currencies } = useCurrency();
  const pkgCurrencyId    = pkg.prices?.[0]?.currencyId ?? null;
  const resolvedCurrency = (pkgCurrencyId ? currencies.find(c => c.id === pkgCurrencyId) : null) ?? activeCurrency;
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const symbol           = resolvedCurrency?.symbol ?? '';
  const baseCostMinor    = pkg.prices?.[0]?.baseCost ?? 0;
  const baseCost         = baseCostMinor / factor;

  const imageUrl = getPackageImage(pkg);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [imageUrl]);

  const pkgName = getPackageName(pkg);
  const isAdded = !!freeTrialEntry;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 16px 28px -8px rgba(13,35,83,0.2)' },
      }}
    >
      <Box sx={{ position: 'relative', height: 140, bgcolor: '#e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
        {imageUrl && !imgError ? (
          <Box
            component="img"
            src={imageUrl}
            alt={pkgName}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Inventory2Icon sx={{ fontSize: 40, color: '#94a3b8' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', p: 1 }}>
          <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: 'rgba(139,92,246,0.85)', fontSize: 9, fontWeight: 700, color: 'white', lineHeight: 1.4 }}>
            Package
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography fontWeight={700} fontSize={13} sx={{ color: '#1e293b', lineHeight: 1.35 }}>
          {pkgName}
        </Typography>
        <Typography fontWeight={800} fontSize={16} sx={{ color: '#1e3a5f' }}>
          {symbol}{baseCost.toFixed(decimalPlaces)}
        </Typography>
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {isAdded ? (
          <>
            <ApprovalStatusBadge
              status={freeTrialEntry.contentStatus}
              actionType={freeTrialEntry.approvalActionType}
            />
            {canEdit && (
              <Button
                size="small"
                startIcon={<RemoveIcon sx={{ fontSize: 13 }} />}
                onClick={() => onRemove(pkg.packageId)}
                disabled={busy}
                sx={{
                  color: '#ef4444', bgcolor: '#fff1f2', borderRadius: '8px', px: 1.25, py: 0.4,
                  fontSize: 11, fontWeight: 700, textTransform: 'none',
                  '&:hover': { bgcolor: '#fee2e2' },
                }}
              >
                Remove
              </Button>
            )}
          </>
        ) : (
          canEdit && (
            <Button
              fullWidth
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => onAdd(pkg.packageId)}
              disabled={busy}
              sx={{
                background: '#059669', color: 'white', fontWeight: 700, fontSize: 11.5,
                textTransform: 'none', borderRadius: '8px', py: 0.6, boxShadow: 'none',
                '&:hover': { background: '#047857', boxShadow: 'none' },
              }}
            >
              Add to Free Trial
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};

export default FreeTrialPackageCard;
